'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { eventLifecycleApi, organizationApi } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { useAuthReady } from '@/lib/use-auth-ready';
import { getEffectiveRole } from '@/lib/rbac';
import { getCreateEventPath } from '@/lib/create-event';
import { navigateStaticDynamicHref } from '@/lib/static-export-nav';
import {
  Calendar,
  Plus,
  Loader2,
  Search,
  Filter,
  Users,
  ChevronDown,
  X,
  Trash2,
  Eye,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { ActionsMenu, type ActionsMenuItem } from '@/components/ui/ActionsMenu';
import { AdminButton } from '@/components/ui/admin';

type AssignmentRow = {
  id?: string;
  organizer_id: string;
  role: string;
  name?: string;
  email?: string;
};

type OrgEvent = {
  id: string;
  title: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  event_owner_type?: string;
  event_owner_id?: string;
  lifecycle_status?: string;
  approval_status?: string;
  created_by?: { id?: string; name?: string; email?: string };
  updated_at?: string;
  assignment_status?: string;
  assignment_count?: number;
  co_host_count?: number;
  lead_organizer?: { id: string; name?: string; email?: string; role?: string } | null;
  assignments?: AssignmentRow[];
  department?: { name?: string };
  club?: { name?: string };
  organizer?: { user?: { name?: string; email?: string } };
  registrations_count?: number;
  event_visibility?: string;
  allowed_email_domains?: string[];
  strict_domain_mode?: boolean;
};

type OrganizerOption = {
  id: string;
  organizer_id?: string;
  name?: string;
  email?: string;
  user?: { name?: string; email?: string };
  membership_id?: string;
};

type PaginatedPayload = {
  data?: OrgEvent[];
  total?: number;
  current_page?: number;
  last_page?: number;
};

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'pending_review', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'live', label: 'Live' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'archived', label: 'Archived' },
];

const OWNER_TYPE_OPTIONS = [
  { value: '', label: 'All owners' },
  { value: 'organization_admin', label: 'Organization' },
  { value: 'organizer', label: 'Organizer' },
  { value: 'individual_organizer', label: 'Individual' },
];

const ROLE_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'co_host', label: 'Co-host' },
  { value: 'support', label: 'Support' },
];

function isDraftEvent(ev: Pick<OrgEvent, 'lifecycle_status' | 'status'>) {
  const raw = (ev.status ?? ev.lifecycle_status ?? '').toLowerCase();
  // Backend only allows delete when `status` is draft.
  return raw === 'draft';
}

function formatDeleteError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> | string[] } } })
    ?.response?.data;
  if (Array.isArray(data?.errors)) {
    return data.errors.join(' ');
  }
  if (data?.errors && typeof data.errors === 'object') {
    return Object.values(data.errors).flat().join(' ');
  }
  return data?.message || fallback;
}

function formatOwnerType(type?: string) {
  if (!type) return null;
  if (type === 'organization_admin') return 'Organization';
  if (type === 'individual_organizer') return 'Individual';
  return type.replace(/_/g, ' ');
}

function formatDate(value?: string) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function LifecycleBadge({ status }: { status?: string }) {
  const raw = (status ?? 'draft').toLowerCase();
  const label = raw.replace(/_/g, ' ');
  const tone =
    raw === 'draft' || raw === 'archived' || raw === 'cancelled' || raw === 'suspended'
      ? 'draft'
      : raw === 'pending_review'
        ? 'pending'
        : 'published';
  return <span className={`org-events-badge org-events-badge--${tone}`}>{label}</span>;
}

function ApprovalBadge({ status, lifecycle }: { status?: string; lifecycle?: string }) {
  const raw = (status ?? '').toLowerCase();
  if (!raw || raw === (lifecycle ?? '').toLowerCase()) return null;
  const label = raw.replace(/_/g, ' ');
  const tone = raw.includes('pending') ? 'pending' : raw === 'approved' ? 'published' : 'draft';
  return <span className={`org-events-badge org-events-badge--${tone}`}>{label}</span>;
}

function AssignmentBadge({ status, coHosts }: { status?: string; coHosts?: number }) {
  const assigned = status === 'assigned';
  return (
    <span className={`org-events-badge ${assigned ? 'org-events-badge--assigned' : 'org-events-badge--unassigned'}`}>
      {assigned ? 'Assigned' : 'Unassigned'}
      {typeof coHosts === 'number' && coHosts > 0 ? ` · ${coHosts} co-host${coHosts !== 1 ? 's' : ''}` : ''}
    </span>
  );
}

function VisibilityChip({ visibility, eventId }: { visibility?: string; eventId: string }) {
  const label = (visibility ?? 'public').replace(/_/g, ' ');
  return (
    <Link href={`/dashboard/events/${eventId}/settings`} className="org-events-visibility-link">
      <Eye className="h-3 w-3" />
      {label}
    </Link>
  );
}

export default function OrganizationEventsDashboardPage() {
  const router = useRouter();
  const authReady = useAuthReady();
  const { user } = useAuthStore();
  const [events, setEvents] = useState<OrgEvent[]>([]);
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [clubs, setClubs] = useState<{ id: string; name: string }[]>([]);
  const [organizerOptions, setOrganizerOptions] = useState<OrganizerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [ownerType, setOwnerType] = useState('');
  const [organizerFilter, setOrganizerFilter] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [clubId, setClubId] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, lastPage: 1 });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTargetIds, setAssignTargetIds] = useState<string[]>([]);
  const [assignOrganizerId, setAssignOrganizerId] = useState('');
  const [assignRole, setAssignRole] = useState<'lead' | 'co_host' | 'support'>('co_host');
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const createEventHref = getCreateEventPath({
    isAuthenticated: Boolean(user),
    canCreateEvents: true,
    workspace: 'organization',
  });

  const goCreateEvent = () => navigateStaticDynamicHref(createEventHref);

  const activeFilterCount = useMemo(() => {
    return [status, ownerType, organizerFilter, departmentId, clubId].filter(Boolean).length;
  }, [status, ownerType, organizerFilter, departmentId, clubId]);

  const selectedDraftIds = useMemo(
    () => events.filter((ev) => selectedIds.includes(ev.id) && isDraftEvent(ev)).map((ev) => ev.id),
    [events, selectedIds]
  );

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventsRes, deptRes, clubRes, orgRes] = await Promise.all([
        organizationApi.events({
          department_id: departmentId || undefined,
          club_id: clubId || undefined,
          status: status || undefined,
          event_owner_type: ownerType || undefined,
          organizer_id: organizerFilter || undefined,
          q: search.trim() || undefined,
          page,
          per_page: 15,
        }),
        departments.length ? Promise.resolve(null) : organizationApi.departments(),
        clubs.length ? Promise.resolve(null) : organizationApi.clubs(),
        organizationApi.organizers({ per_page: 100 }),
      ]);

      const payload = eventsRes.data?.data as PaginatedPayload | OrgEvent[] | undefined;
      const list = Array.isArray(payload) ? payload : payload?.data ?? [];
      setEvents(list);
      if (payload && !Array.isArray(payload)) {
        setPagination({
          total: payload.total ?? list.length,
          lastPage: payload.last_page ?? 1,
        });
      }

      if (deptRes?.data?.data) setDepartments(deptRes.data.data);
      if (clubRes?.data?.data) setClubs(clubRes.data.data);
      if (orgRes?.data?.data?.data) {
        setOrganizerOptions(orgRes.data.data.data);
      } else if (orgRes?.data?.data && Array.isArray(orgRes.data.data)) {
        setOrganizerOptions(orgRes.data.data);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to load organization events.';
      setError(message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [
    clubId,
    clubs.length,
    departmentId,
    departments.length,
    organizerFilter,
    ownerType,
    page,
    search,
    status,
  ]);

  const fetchOrganizerOptions = useCallback(async () => {
    try {
      const orgRes = await organizationApi.organizers({ per_page: 100 });
      const rows = orgRes.data?.data?.data ?? orgRes.data?.data ?? [];
      if (Array.isArray(rows)) setOrganizerOptions(rows);
    } catch (_) {
      // Ignore
    }
  }, []);

  const openAssign = (eventIds: string[]) => {
    setAssignTargetIds(eventIds);
    setAssignOrganizerId('');
    setAssignRole('co_host');
    setAssignError(null);
    setAssignOpen(true);
    void fetchOrganizerOptions();
  };

  useEffect(() => {
    if (!authReady) return;
    void loadEvents();
  }, [authReady, loadEvents]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { draft: 0, published: 0, pending_review: 0 };
    events.forEach((ev) => {
      const key = (ev.lifecycle_status || ev.status || 'unknown').toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return counts;
  }, [events]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    if (authReady) void loadEvents();
  };

  const clearFilters = () => {
    setStatus('');
    setOwnerType('');
    setOrganizerFilter('');
    setDepartmentId('');
    setClubId('');
    setSearch('');
    setPage(1);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === events.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(events.map((e) => e.id));
    }
  };

  const deleteDraftEvents = async (eventIds: string[]) => {
    const draftIds = events
      .filter((ev) => eventIds.includes(ev.id) && isDraftEvent(ev))
      .map((ev) => ev.id);

    if (draftIds.length === 0) {
      const msg = 'Only draft events can be deleted. Published or approved events must stay in the list or be archived.';
      setError(msg);
      toast.error(msg);
      return;
    }

    const skipped = eventIds.length - draftIds.length;
    const confirmMsg =
      draftIds.length === 1
        ? 'Delete this draft event? This cannot be undone.'
        : `Delete ${draftIds.length} draft event${draftIds.length !== 1 ? 's' : ''}? This cannot be undone.${
            skipped > 0 ? `\n\n${skipped} non-draft event(s) will be skipped.` : ''
          }`;

    if (!window.confirm(confirmMsg)) return;

    setDeleting(true);
    setError(null);
    try {
      const res = await organizationApi.bulkEvents({
        action: 'delete_draft',
        event_ids: draftIds,
      });
      const result = res.data?.data as { processed?: number; errors?: string[] } | undefined;
      const errors = result?.errors ?? [];
      const processed = Number(result?.processed ?? 0);

      if (processed > 0) {
        toast.success(`Deleted ${processed} draft event${processed !== 1 ? 's' : ''}.`);
        setSelectedIds((prev) => prev.filter((id) => !draftIds.includes(id)));
        await loadEvents();
      }

      if (errors.length > 0) {
        const msg = errors.join(' ');
        setError(msg);
        toast.error(msg);
        if (processed > 0) {
          await loadEvents();
        }
      } else if (processed === 0) {
        const msg = 'No events were deleted.';
        setError(msg);
        toast.error(msg);
      }
    } catch (err: unknown) {
      const message = formatDeleteError(err, 'Failed to delete event(s).');
      setError(message);
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const runOperation = async (
    eventId: string,
    action: 'publish' | 'archive' | 'suspend' | 'duplicate' | 'approve' | 'delete_draft'
  ) => {
    try {
      if (action === 'delete_draft') {
        await deleteDraftEvents([eventId]);
        return;
      }
      if (action === 'publish') await eventLifecycleApi.publish(eventId);
      if (action === 'archive') await eventLifecycleApi.archive(eventId);
      if (action === 'suspend') await eventLifecycleApi.suspend(eventId);
      if (action === 'duplicate') await eventLifecycleApi.duplicate(eventId);
      if (action === 'approve') await eventLifecycleApi.approve(eventId);
      await loadEvents();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Operation failed.';
      setError(message);
    }
  };

  const submitAssign = async () => {
    if (!assignOrganizerId || assignTargetIds.length === 0) {
      setAssignError('Select an organizer to assign.');
      return;
    }
    setAssignSaving(true);
    setAssignError(null);
    try {
      for (const eventId of assignTargetIds) {
        await organizationApi.assignOrganizers(eventId, [
          { organizer_id: assignOrganizerId, role: assignRole },
        ]);
      }
      setAssignOpen(false);
      setSelectedIds([]);
      await loadEvents();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
          ?.response?.data?.message ||
        (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
          ?.errors?.organizer_id?.[0] ||
        'Failed to assign organizer.';
      setAssignError(message);
    } finally {
      setAssignSaving(false);
    }
  };

  const actionsFor = (ev: OrgEvent): ActionsMenuItem[] => {
    const items: ActionsMenuItem[] = [
      {
        key: 'overview',
        label: 'Overview',
        onClick: () => router.push(`/dashboard/events/${ev.id}/overview`),
      },
      {
        key: 'assign',
        label: 'Assign organizer',
        onClick: () => openAssign([ev.id]),
      },
      {
        key: 'promotion',
        label: 'Promotion',
        onClick: () => router.push(`/dashboard/organization/events/${ev.id}/promotion`),
      },
      {
        key: 'open',
        label: 'Open event',
        onClick: () => router.push(`/dashboard/events/${ev.id}`),
      },
    ];
    if (isDraftEvent(ev)) {
      items.push({
        key: 'delete',
        label: 'Delete',
        variant: 'danger',
        disabled: deleting,
        onClick: () => void deleteDraftEvents([ev.id]),
      });
    }
    return items;
  };

  const EventMeta = ({ ev }: { ev: OrgEvent }) => {
    const date = formatDate(ev.start_date);
    const owner = formatOwnerType(ev.event_owner_type);
    const parts = [date, ev.department?.name, ev.club?.name, owner].filter(Boolean);
    if (parts.length === 0) return null;
    return <p className="org-events-row-sub">{parts.join(' · ')}</p>;
  };

  const renderEventRow = (ev: OrgEvent) => (
    <article key={ev.id} className="org-events-row">
      <div className="org-events-mobile-check">
        <input
          type="checkbox"
          checked={selectedIds.includes(ev.id)}
          onChange={() => toggleSelect(ev.id)}
          aria-label={`Select ${ev.title}`}
          className="rounded border-nature-border lg:hidden"
        />
        <div className="org-events-row-check">
          <input
            type="checkbox"
            checked={selectedIds.includes(ev.id)}
            onChange={() => toggleSelect(ev.id)}
            aria-label={`Select ${ev.title}`}
            className="rounded border-nature-border"
          />
        </div>
        <Link href={`/dashboard/events/${ev.id}`} className="org-events-row-main min-w-0 flex-1">
          <div className="org-events-row-icon">{(ev.title || 'E').trim().charAt(0).toUpperCase()}</div>
          <div className="min-w-0">
            <span className="org-events-row-title block truncate">{ev.title || 'Untitled Event'}</span>
            <EventMeta ev={ev} />
          </div>
        </Link>
      </div>

      <div className="org-events-row-cell org-events-row-cell--status" data-label="Status">
        <LifecycleBadge status={ev.lifecycle_status ?? ev.status} />
        <ApprovalBadge status={ev.approval_status} lifecycle={ev.lifecycle_status ?? ev.status} />
      </div>

      <div className="org-events-row-cell org-events-row-cell--visibility" data-label="Visibility">
        <VisibilityChip visibility={ev.event_visibility} eventId={ev.id} />
      </div>

      <div className="org-events-row-cell org-events-row-cell--team" data-label="Team">
        <span className="org-events-row-team-name">
          {ev.lead_organizer?.name ?? ev.lead_organizer?.email ?? 'No lead yet'}
        </span>
        <AssignmentBadge status={ev.assignment_status} coHosts={ev.co_host_count} />
      </div>

      <div className="org-events-row-actions">
        <ActionsMenu items={actionsFor(ev)} />
      </div>
    </article>
  );

  return (
    <div className="org-events-page">
      <header className="org-events-hero">
        <div className="org-events-hero-glow" aria-hidden />
        <div className="org-events-hero-top">
          <div>
            <p className="org-events-hero-kicker">Organization · Events</p>
            <h1 className="org-events-hero-title">Organization Events</h1>
            <p className="org-events-hero-desc">
              Manage events across your organization
              {getEffectiveRole(user) ? ` · ${getEffectiveRole(user).replace(/_/g, ' ')}` : ''}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {selectedIds.length > 0 ? (
              <>
                <AdminButton
                  variant="outline"
                  icon={Users}
                  onClick={() => openAssign(selectedIds)}
                  disabled={deleting}
                  className="!border-white/25 !bg-bg/10 !text-plaster hover:!bg-bg/15"
                >
                  Assign ({selectedIds.length})
                </AdminButton>
                {selectedDraftIds.length > 0 ? (
                  <AdminButton
                    variant="outline"
                    icon={Trash2}
                    loading={deleting}
                    onClick={() => void deleteDraftEvents(selectedIds)}
                    className="!border-white/25 !bg-bg/10 !text-plaster hover:!bg-bg/15"
                  >
                    Delete ({selectedDraftIds.length})
                  </AdminButton>
                ) : null}
              </>
            ) : null}
            <AdminButton icon={Plus} onClick={goCreateEvent} className="!bg-plaster !text-soot hover:!bg-bg">
              Create event
            </AdminButton>
          </div>
        </div>
      </header>

      {error ? (
        <div className="rounded-xl border border-soot/20 bg-soot/5 px-4 py-3 text-sm text-soot">{error}</div>
      ) : null}

      <section className="org-events-panel">
        <form onSubmit={handleSearchSubmit} className="org-events-toolbar">
          <div className="org-events-search-wrap">
            <Search className="org-events-search-icon h-4 w-4" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events…"
              className="org-events-search"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-moss hover:text-soot"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="org-events-select"
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={organizerFilter}
            onChange={(e) => {
              setOrganizerFilter(e.target.value);
              setPage(1);
            }}
            className="org-events-select"
            aria-label="Filter by organizer"
          >
            <option value="">All organizers</option>
            {organizerOptions.map((o) => (
              <option key={o.organizer_id ?? o.id} value={o.organizer_id ?? o.id}>
                {o.name || o.user?.name || o.email || o.user?.email || o.id}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="org-events-toolbar-btn org-events-toolbar-btn--badge"
          >
            <Filter className="h-3.5 w-3.5" />
            More
            {activeFilterCount > 0 ? (
              <span className="org-events-filter-badge">{activeFilterCount}</span>
            ) : null}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
          </button>

          <AdminButton type="submit" size="sm">
            Search
          </AdminButton>

          {(activeFilterCount > 0 || search) ? (
            <button type="button" onClick={clearFilters} className="org-events-toolbar-btn">
              Clear
            </button>
          ) : null}

          {!loading && events.length > 0 ? (
            <div className="org-events-toolbar-meta">
              <span>
                <strong>{events.length}</strong>
                {pagination.total > events.length ? ` of ${pagination.total}` : ''} events
              </span>
              {selectedIds.length > 0 ? (
                <span className="org-events-selected-chip">{selectedIds.length} selected</span>
              ) : null}
              <label>
                <input
                  type="checkbox"
                  checked={selectedIds.length === events.length && events.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-nature-border"
                />
                Select all
              </label>
            </div>
          ) : null}
        </form>

        {filtersOpen ? (
          <div className="org-events-filters-extra">
            <select
              value={ownerType}
              onChange={(e) => {
                setOwnerType(e.target.value);
                setPage(1);
              }}
              className="org-events-select w-full"
            >
              {OWNER_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value || 'all-owner'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
                setPage(1);
              }}
              className="org-events-select w-full"
            >
              <option value="">All departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            <select
              value={clubId}
              onChange={(e) => {
                setClubId(e.target.value);
                setPage(1);
              }}
              className="org-events-select w-full"
            >
              <option value="">All clubs</option>
              {clubs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {!authReady || loading ? (
          <div className="org-events-loading">
            <Loader2 className="h-7 w-7 animate-spin text-eucalyptus" />
          </div>
        ) : events.length === 0 ? (
          <div className="org-events-empty">
            <div className="org-events-empty-icon">
              <Calendar className="h-6 w-6" />
            </div>
            <p className="text-base font-semibold text-soot">No events found</p>
            <p className="mt-1 text-sm text-moss">
              {activeFilterCount > 0 || search
                ? 'Try adjusting your search or filters.'
                : 'Create your first organization event to get started.'}
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {activeFilterCount > 0 || search ? (
                <AdminButton variant="outline" onClick={clearFilters}>
                  Clear filters
                </AdminButton>
              ) : null}
              <AdminButton icon={Plus} onClick={goCreateEvent}>
                Create event
              </AdminButton>
            </div>
          </div>
        ) : (
          <>
            <div className="org-events-table-head" aria-hidden>
              <span />
              <span>Event</span>
              <span>Status</span>
              <span>Visibility</span>
              <span>Team</span>
              <span>Actions</span>
            </div>
            <div className="org-events-list">{events.map(renderEventRow)}</div>
          </>
        )}
      </section>

      {pagination.lastPage > 1 ? (
        <div className="org-events-pagination">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="org-events-page-btn"
          >
            Previous
          </button>
          <span className="org-events-page-indicator">
            Page {page} of {pagination.lastPage}
          </span>
          <button
            type="button"
            disabled={page >= pagination.lastPage || loading}
            onClick={() => setPage((p) => p + 1)}
            className="org-events-page-btn"
          >
            Next
          </button>
        </div>
      ) : null}

      {assignOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md space-y-4 rounded-2xl border border-nature-border bg-white p-6 shadow-xl"
          >
            <div>
              <h2 className="text-lg font-bold text-soot">
                Assign organizer
                {assignTargetIds.length > 1 ? ` · ${assignTargetIds.length} events` : ''}
              </h2>
              <p className="mt-1 text-sm text-moss">Choose who will lead or support these events.</p>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-soot">Organizer</label>
              <select
                value={assignOrganizerId}
                onChange={(e) => setAssignOrganizerId(e.target.value)}
                className="org-events-select w-full"
              >
                <option value="">Select organizer…</option>
                {organizerOptions.map((o) => (
                  <option key={o.organizer_id ?? o.id} value={o.organizer_id ?? o.id}>
                    {o.name || o.user?.name || o.email || o.user?.email || o.id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-soot">Role</label>
              <select
                value={assignRole}
                onChange={(e) => setAssignRole(e.target.value as 'lead' | 'co_host' | 'support')}
                className="org-events-select w-full"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            {assignError ? <p className="text-sm text-soot">{assignError}</p> : null}
            <div className="flex justify-end gap-2 pt-2">
              <AdminButton variant="outline" onClick={() => setAssignOpen(false)} disabled={assignSaving}>
                Cancel
              </AdminButton>
              <AdminButton onClick={() => void submitAssign()} disabled={assignSaving}>
                {assignSaving ? 'Assigning…' : 'Assign'}
              </AdminButton>
            </div>
          </motion.div>
        </div>
      ) : null}
    </div>
  );
}
